import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from 'antd';
import {
  useGetClientContextQuery,
  useGetClientDepartmentsQuery,
} from '../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Client Admin — Departments.
 * The platform's departments enabled for the client's state (scoped to the
 * logged-in user's org — no state code passed). The client opens the ones they
 * follow up with; how each level is built is set by the platform.
 * ------------------------------------------------------------------ */

export default function ClientDepartmentsPage() {
  const navigate = useNavigate();
  const { data: ctx } = useGetClientContextQuery();
  const { data: departments = [], isLoading } = useGetClientDepartmentsQuery();
  const stateName = ctx?.state_name ?? 'your state';
  const [reqName, setReqName] = useState('');
  const [requests, setRequests] = useState<string[]>([]);

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">CLIENT SETUP · FROM THE PLATFORM</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Departments</h1>
        <p className="max-w-[780px] text-sm leading-6 text-ink-secondary">
          {departments.length} departments are ready for {stateName}, with their territories and
          officers. Open the ones you follow up with, and only the levels you need. How each level is
          built is set by the platform.
        </p>
      </section>

      <section className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            {isLoading && (
              <div className="px-[18px] py-4 text-[13px] text-ink-secondary">Loading departments…</div>
            )}
            {!isLoading && departments.length === 0 && (
              <div className="px-[18px] py-4 text-[13px] text-ink-secondary">
                No departments enabled for {stateName} yet.
              </div>
            )}
            {departments.map((d) => {
              const inUse = Boolean(d.activated);
              return (
                <div
                  key={d.code}
                  className="grid grid-cols-[minmax(220px,1.4fr)_minmax(200px,1fr)_120px_minmax(200px,auto)] items-center gap-3 border-t border-[#f3f4f6] px-[18px] py-3 first:border-t-0"
                >
                  <span className="font-semibold text-ink">{d.name}</span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-xs text-ink-secondary">
                      {d.level_count} level{d.level_count === 1 ? '' : 's'}
                      {d.control ? ` · ${d.control}` : ''}
                    </span>
                    {d.coverage != null && (
                      <span className="text-xs text-ink-secondary">{d.coverage}% coverage</span>
                    )}
                  </span>
                  <span className={['text-xs font-semibold', inUse ? 'text-brand' : 'text-ink-secondary'].join(' ')}>
                    {inUse ? 'In use' : 'Available'}
                  </span>
                  <span className="flex flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => navigate(`/client/departments/${d.code}/view`)}
                      className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/client/departments/${d.code}`)}
                      className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                    >
                      Change levels
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <h2 className="text-lg font-bold text-ink">Need a department that isn’t here?</h2>
        <span className="text-[13px] text-ink-secondary">
          The platform team adds it as an ad-hoc department, so other {stateName} clients can use it
          too.
        </span>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-1 basis-[280px] flex-col gap-1">
            <span className="text-xs font-semibold text-ink-secondary">Department name</span>
            <Input size="large" value={reqName} onChange={(e) => setReqName(e.target.value)} placeholder="e.g. State Road Transport Corporation" />
          </label>
          <button
            type="button"
            onClick={() => {
              if (!reqName.trim()) return;
              setRequests((r) => [...r, reqName.trim()]);
              setReqName('');
            }}
            className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            Send request
          </button>
        </div>
        {requests.map((q) => (
          <div key={q} className="border-t border-[#f3f4f6] py-1.5 text-[13px] text-ink">
            <b>{q}</b> · Waiting for review
          </div>
        ))}
      </section>
    </div>
  );
}
